import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

interface Banner {
  id: number;
  image: string;
  title: string;
  subtitle: string;
  buttonText: string;
  link: string;
}

const BannerSlider: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentBanner, setCurrentBanner] = useState(0);

  const banners: Banner[] = [
    {
      id: 1,
      image: '/banner4.png',
      title: t('banner.saturos_pro_title'),
      subtitle: t('banner.saturos_pro_subtitle'),
      buttonText: t('banner.explore_now_button'),
      link: '/plugins/saturos-pro'
    },
    {
      id: 2,
      image: '/banner5.png', 
      title: t('banner.summer_title'),
      subtitle: t('banner.summer_subtitle'),
      buttonText: t('banner.shop_new_button'),
      link: '/plugins'
    },
    // {
    //   id: 3,
    //   image: '/banner6.png',
    //   title: 'Special Offers',
    //   subtitle: 'Limited Time Deals on Top VSTs',
    //   buttonText: 'Get Deals',
    //   link: '/products'
    // },
    // {
    //   id: 4,
    //   image: '/banner7.png',
    //   title: 'Pro Studio',
    //   subtitle: 'Professional Music Production Tools',
    //   buttonText: 'Go Pro',
    //   link: '/products'
    // }
  ];

  // Auto-advance banners
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 8000); // Change every 8 seconds

    return () => clearInterval(interval);
  }, [banners.length]);

  const goToSlide = (index: number) => {
    setCurrentBanner(index);
  };

  const nextSlide = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevSlide = () => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  };

  return (
    <div className="relative w-full h-[400px] md:h-[500px] overflow-hidden bg-black -mt-1 max-w-full">
      {/* Banner Content */}
      <div className="relative w-full h-full">
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentBanner ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            {/* Desktop Layout - Side by Side */}
            <div className="hidden md:flex h-full">
              {/* Text Content - Left Side */}
              <div className="w-1/2 flex items-center justify-center p-8 bg-black">
                <div className="max-w-md text-left">
                  <h1 className="text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                    {banner.title}
                    {banner.id === 1 && (
                      <span className="ml-3 text-sm lg:text-base bg-red-600 text-white px-2 py-1 rounded-full font-medium align-super">
                        {t('banner.new_badge')}
                      </span>
                    )}
                    {banner.id === 2 && (
                      <span className="ml-3 text-3xl lg:text-5xl bg-red-600 text-white px-4 py-2 rounded-full font-bold">
                        {t('banner.sale_badge')}
                      </span>
                    )}
                  </h1>
                  <p className="text-lg lg:text-xl text-gray-200 mb-8 leading-relaxed">
                    {banner.subtitle}
                  </p>
                  <button
                    onClick={() => {
                      // Only navigate if this is the currently visible banner
                      if (index === currentBanner) {
                        navigate(banner.link);
                      }
                    }}
                    className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
                  >
                    {banner.buttonText}
                  </button>
                </div>
              </div>
              
              {/* Image - Right Side */}
              <div className="w-1/2 overflow-hidden">
                <img 
                  src={banner.image} 
                  alt={banner.title}
                  className="w-full h-full object-cover cursor-pointer transition-transform duration-300 hover:scale-105"
                  onClick={() => {
                    if (index === currentBanner) {
                      navigate(banner.link);
                    }
                  }}
                />
              </div>
            </div>

            {/* Mobile Layout - Stacked */}
            <div className="md:hidden h-full flex flex-col">
              {/* Image - Top */}
              <div className="h-3/5 overflow-hidden">
                <img 
                  src={banner.image} 
                  alt={banner.title}
                  className="w-full h-full object-cover cursor-pointer transition-transform duration-300 hover:scale-105"
                  onClick={() => {
                    if (index === currentBanner) {
                      navigate(banner.link);
                    }
                  }}
                />
              </div>
              
              {/* Text Content - Bottom */}
              <div className="h-2/5 flex items-center justify-center p-6 bg-black">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-white mb-3 leading-tight">
                    {banner.title}
                    {banner.id === 1 && (
                      <span className="ml-2 text-xs bg-red-600 text-white px-2 py-1 rounded-full font-medium align-super">
                        {t('banner.new_badge')}
                      </span>
                    )}
                    {banner.id === 2 && (
                      <span className="ml-2 text-2xl bg-red-600 text-white px-3 py-1 rounded-full font-bold">
                        {t('banner.sale_badge')}
                      </span>
                    )}
                  </h1>
                  <p className="text-sm text-gray-200 mb-6 leading-relaxed">
                    {banner.subtitle}
                  </p>
                  <button
                    onClick={() => {
                      // Only navigate if this is the currently visible banner
                      if (index === currentBanner) {
                        navigate(banner.link);
                      }
                    }}
                    className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-2 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    {banner.buttonText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dots Indicator - Mobile */}
      <div className="absolute left-1/2 transform -translate-x-1/2 z-20 flex space-x-3 md:hidden"
           style={{ bottom: 'calc(40% + 1rem)' }}>
        {banners.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentBanner 
                ? 'bg-white shadow-lg' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>

      {/* Dots Indicator - Desktop */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3 hidden md:flex">
        {banners.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentBanner 
                ? 'bg-white shadow-lg' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default BannerSlider; 